import React from 'react';

import db from '../../lib/db';
import { escapeRegex } from '../../lib/stringUtils';

import { KEYCHAIN } from '../../contexts/KeyMaster';
import { ChannelList } from './ChannelList';
import SuggestionPortal, { createSuggestionRender } from './SuggestionPortal';

const ChannelSuggestionPortal = ({ clientRect, onClose, ...listProps }) => (
  <SuggestionPortal clientRect={clientRect} onClose={onClose} keychainId={KEYCHAIN.POPOVER__CHANNEL_SUGGESTION}>
    <ChannelList {...listProps} />
  </SuggestionPortal>
);

export default function createChannelSuggestion({ getWorkspaceId = () => null, getMainChannelId = () => null } = {}) {
  return {
    char: '#',
    // This function is used by TipTap to determine whether to show the popup
    // We still query here for that logic, but ChannelList does its own reactive query
    items: async function fetchSuggestionItems({ query }) {
      // pin workspace_id at entry — this is an async flow, don't re-read the observer mid-retry
      const wsId = getWorkspaceId();
      if (!wsId) return [];

      const mainChannelId = getMainChannelId();

      // Bare "#" (empty query) matches everything so the popup opens immediately;
      // ChannelList does its own reactive query for the actual ordering.
      const regex = new RegExp(escapeRegex(query || ''), 'i');
      // Prefilter by [workspace_id+kind] (indexed), then regex filter on name. Limit 5 for quick check.
      const channels = await db.channels
        .where('[workspace_id+kind]')
        .anyOf([
          [wsId, 'public'],
          [wsId, 'open_breakout'],
        ])
        .filter(function matchChannel(c) {
          if (!c?.name) return false;
          if (mainChannelId && String(c.id) === String(mainChannelId)) return false;
          return regex.test(c.name);
        })
        .limit(5)
        .toArray();

      const results = channels.filter((c) => !!c?.name);

      if (mainChannelId) {
        // Main displays as the workspace title (home-row rule), so match on that.
        const workspace = await db.workspaces.get(wsId);
        if (workspace?.title && regex.test(workspace.title)) {
          const mainChannel = await db.channels.get([wsId, mainChannelId]);
          if (mainChannel) {
            results.push(mainChannel);
          }
        }
      }

      return results;
    },

    allowSpaces: false,
    command: ({ editor, range, props }) => {
      // increase range.to by one when the next node is of type "text"
      // and starts with a space character
      const nodeAfter = editor.view.state.selection.$to.nodeAfter;
      const overrideSpace = nodeAfter?.text?.startsWith(' ');

      if (overrideSpace) {
        range.to += 1;
      }

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          { type: 'channel', attrs: { id: props.id, label: props.name } },
          { type: 'text', text: ' ' },
        ])
        .run();

      // get reference to `window` object from editor element, to support cross-frame JS usage
      editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
    },

    render: createSuggestionRender(ChannelSuggestionPortal),
  };
}
